import React, { useState } from 'react';
import { CheckCircle, XCircle, HelpCircle, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';

const MiniQuiz = ({ question, options, correctAnswer, explanation, onComplete }) => {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const handleSelect = (index) => {
    if (!isSubmitted) {
      setSelectedAnswer(index);
    }
  };

  const handleSubmit = () => {
    if (selectedAnswer === null) return;
    
    const correct = selectedAnswer === correctAnswer;
    setIsCorrect(correct);
    setIsSubmitted(true);
    
    if (onComplete) {
      onComplete(correct);
    }
  };

  const handleReset = () => {
    setSelectedAnswer(null);
    setIsSubmitted(false);
    setIsCorrect(false);
  };

  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200">
      <div className="flex items-center gap-2 mb-4">
        <HelpCircle className="w-5 h-5 text-emerald-600" />
        <span className="font-semibold text-slate-800">Knowledge Check</span>
      </div>
      
      <p className="text-slate-700 font-medium mb-4">{question}</p>
      
      <div className="space-y-2 mb-4">
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleSelect(index)}
            disabled={isSubmitted}
            className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
              isSubmitted
                ? index === correctAnswer
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                  : index === selectedAnswer
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-slate-200 bg-white text-slate-500'
                : selectedAnswer === index
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50/50'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                isSubmitted
                  ? index === correctAnswer
                    ? 'border-emerald-500 bg-emerald-500'
                    : index === selectedAnswer
                      ? 'border-red-500 bg-red-500'
                      : 'border-slate-300'
                  : selectedAnswer === index
                    ? 'border-emerald-500 bg-emerald-500'
                    : 'border-slate-300'
              }`}>
                {isSubmitted && index === correctAnswer && (
                  <CheckCircle className="w-4 h-4 text-white" />
                )}
                {isSubmitted && index === selectedAnswer && index !== correctAnswer && (
                  <XCircle className="w-4 h-4 text-white" />
                )}
                {!isSubmitted && selectedAnswer === index && (
                  <div className="w-2 h-2 bg-white rounded-full" />
                )}
              </div>
              <span className="text-sm">{option}</span>
            </div>
          </button>
        ))}
      </div>

      {isSubmitted && explanation && (
        <div className={`p-4 rounded-lg mb-4 ${isCorrect ? 'bg-emerald-100 border border-emerald-200' : 'bg-amber-100 border border-amber-200'}`}>
          <p className={`text-sm ${isCorrect ? 'text-emerald-700' : 'text-amber-700'}`}>
            <strong>{isCorrect ? '✓ Correct!' : '✗ Not quite.'}</strong> {explanation}
          </p>
        </div>
      )}

      <div className="flex gap-2">
        {!isSubmitted ? (
          <Button 
            onClick={handleSubmit}
            disabled={selectedAnswer === null}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            Check Answer
          </Button>
        ) : (
          <Button 
            onClick={handleReset}
            variant="outline"
            className="border-slate-300"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        )}
      </div>
    </div>
  );
};

// Pre-built knowledge check questions for each module
export const lessonQuizzes = {
  1: {
    1: { // Module 1, Lesson 1
      question: "When was OSHA created?",
      options: [
        "1960",
        "1970",
        "1980",
        "1990"
      ],
      correctAnswer: 1,
      explanation: "OSHA was created by the Occupational Safety and Health Act of 1970, signed into law by President Nixon on December 29, 1970."
    },
    2: { // Module 1, Lesson 2
      question: "Which of the following is NOT a worker right under OSHA?",
      options: [
        "Right to request an OSHA inspection",
        "Right to receive training in a language you understand",
        "Right to set your own work schedule",
        "Right to report hazards without retaliation"
      ],
      correctAnswer: 2,
      explanation: "Workers have many OSHA rights including requesting inspections, training, and reporting hazards. Setting work schedules is not an OSHA right."
    },
    3: { // Module 1, Lesson 3
      question: "What is the General Duty Clause?",
      options: [
        "A requirement to post safety signs",
        "A requirement to provide a hazard-free workplace",
        "A requirement to conduct annual training",
        "A requirement to report all injuries"
      ],
      correctAnswer: 1,
      explanation: "Section 5(a)(1), the General Duty Clause, requires employers to furnish a workplace free from recognized hazards."
    }
  },
  2: {
    1: { // Module 2, Lesson 1
      question: "Which is NOT one of the six categories of workplace hazards?",
      options: [
        "Physical hazards",
        "Financial hazards",
        "Biological hazards",
        "Ergonomic hazards"
      ],
      correctAnswer: 1,
      explanation: "The six categories are: Physical, Chemical, Biological, Ergonomic, Safety, and Psychosocial. Financial hazards are not a category of workplace hazards."
    },
    2: { // Module 2, Lesson 2
      question: "What does JHA stand for?",
      options: [
        "Job Health Assessment",
        "Job Hazard Analysis",
        "Joint Hazard Awareness",
        "Justified Hazard Action"
      ],
      correctAnswer: 1,
      explanation: "JHA stands for Job Hazard Analysis, a technique that focuses on job tasks to identify hazards before they occur."
    },
    3: { // Module 2, Lesson 3
      question: "Which control is MOST effective in the Hierarchy of Controls?",
      options: [
        "PPE",
        "Administrative controls",
        "Elimination",
        "Engineering controls"
      ],
      correctAnswer: 2,
      explanation: "Elimination is the most effective control because it completely removes the hazard. PPE is the least effective as it's the last line of defense."
    }
  },
  5: {
    2: { // Module 5, Lesson 2 (LOTO)
      question: "How many steps are in the Lockout/Tagout procedure?",
      options: [
        "Four",
        "Five",
        "Six",
        "Seven"
      ],
      correctAnswer: 2,
      explanation: "The six LOTO steps are: Preparation, Notification, Shutdown, Isolation, Lockout/Tagout, and Verification."
    }
  },
  6: {
    2: { // Module 6, Lesson 2 (Lifting)
      question: "When lifting, you should bend at the:",
      options: [
        "Waist",
        "Knees",
        "Hips",
        "Ankles"
      ],
      correctAnswer: 1,
      explanation: "Always bend at the knees, not the waist, when lifting. This uses your strong leg muscles and protects your back."
    }
  }
};

export default MiniQuiz;
